<template>
  <TabGroup
    v-if="product"
    class="product-tabs"
    :tabs="tabGroupEntries"
    :active="activeEntry"
  >
    <!-- Highlights -->
    <template #highlights>
      <OfferHighlights :offer="offer" />
    </template>

    <!-- Delivered Items -->
    <template #deliveredItems>
      <DeliveredItems :offer="offer" />
    </template>

    <!-- Compatibility -->
    <template #compatibility>
      <CompatibleProductFamilies :offer="offer" />
    </template>

    <!-- Features / Technical Details -->
    <template #features>
      <FeatureList :offer="offer" />
    </template>

    <!-- Produktsicherheit -->
    <template #productSecurity>
      <ProductSecurity :offer="offer" />
    </template>
  </TabGroup>
</template>

<script setup lang="ts">
import { computed, type PropType } from 'vue';
import { offerHasUsps } from '~/lib/goliath/offer-usps';
import CompatibleProductFamilies from '~/components/product-detail/tabs/compatible-product-families.vue';
import DeliveredItems from '~/components/product-detail/tabs/delivered-items.vue';
import FeatureList from '~/components/product-detail/tabs/feature-list.vue';
import OfferHighlights from '~/components/product-detail/tabs/offer-highlights.vue';
import ProductSecurity from '~/components/product-detail/tabs/product-security.vue';
import TabGroup from '~/components/tab-group.vue';

import {
  getBundleProduct,
  getStandardProduct,
  type Offer,
  type OfferBundleProduct,
  type OfferProduct,
  type OfferStandardProduct,
} from '~/lib/goliath/offer-by-slugs';

const props = defineProps({
  offer: {
    type: Object as PropType<Offer>,
    required: true,
  },
  activeEntry: {
    type: Number,
    default: 0,
  },
});

const product = computed(() => props.offer?.product);

const containedProducts = computed<
  (OfferStandardProduct | NonNullable<NonNullable<OfferBundleProduct['bundledProducts']>[number]>)[]
>(() => {
  if (!product.value) return [];
  const bundleProduct = getBundleProduct(product.value);
  if (bundleProduct) {
    return bundleProduct.bundledProducts || [];
  }
  const standardProduct = getStandardProduct(product.value);
  if (standardProduct) {
    return [standardProduct];
  }
  return [];
});

const hasHighlights = computed(() => offerHasUsps(props.offer));

const hasDeliveredItems = computed(() =>
  containedProducts.value.some((p) => p.deliveredItems && p.deliveredItems.length)
);

const hasCompatibilities = computed(() =>
  containedProducts.value.some((p) => (p?.family?.compatibleFamilies?.length || 0) > 0)
);

const hasFeatures = computed(() =>
  containedProducts.value.some(
    (p: OfferProduct) =>
      (p?.features && p.features.length) || p.eans?.length > 0 || p.commonCharger?.possible === true
  )
);

const hasSecurity = computed(() =>
  containedProducts.value.some(
    (p: OfferProduct) =>
      p.family.manufacturerContact || p.family.manufacturerContactEu || p.securityInformationUrl
  )
);

const tabGroupEntries = computed(() => {
  const data = [];
  if (hasHighlights.value) {
    data.push({ name: 'highlights', label: 'Highlights' });
  }
  if (hasDeliveredItems.value) {
    data.push({ name: 'deliveredItems', label: 'Lieferumfang' });
  }
  if (hasCompatibilities.value) {
    data.push({ name: 'compatibility', label: 'Kompatibilität' });
  }
  if (hasFeatures.value) {
    data.push({ name: 'features', label: 'Technische Details' });
  }
  if (hasSecurity.value) {
    data.push({ name: 'productSecurity', label: 'Produktsicherheit' });
  }
  return data;
});
</script>
